import { type Accessor, createEffect, createSignal, onCleanup } from "solid-js"

export type VisualViewportGeometry = {
  /** Layout viewport height (`window.innerHeight`). */
  innerHeight: number
  /**
   * The visible part of the layout viewport, as it last was unzoomed. Absent
   * when the browser has no VisualViewport API, or the page was zoomed throughout.
   */
  view?: { height: number; offsetTop: number }
}

// One owner for "the visible viewport moved or resized": keyboard, rotation and
// bfcache restores. Every consumer shares these listeners. A pinch zoom only
// magnifies the page, yet pans the visual viewport on every frame, so while
// zoomed the view stays as it last was unzoomed and unchanged geometry notifies
// no one: a pinch re-runs no consumer.
const sameGeometry = (a: VisualViewportGeometry | undefined, b: VisualViewportGeometry | undefined) =>
  a === b ||
  (!!a && !!b && a.innerHeight === b.innerHeight && a.view?.height === b.view?.height && a.view?.offsetTop === b.view?.offsetTop)
const [geometry, setGeometry] = createSignal<VisualViewportGeometry | undefined>(undefined, { equals: sameGeometry })
let subscribers = 0
let attached: { view: VisualViewport | null; detach: () => void } | undefined

function read(previous: VisualViewportGeometry | undefined): VisualViewportGeometry {
  const view = window.visualViewport
  if (view && (view.scale ?? 1) !== 1) return { innerHeight: window.innerHeight, view: previous?.view }
  return {
    innerHeight: window.innerHeight,
    view: view ? { height: view.height, offsetTop: view.offsetTop } : undefined,
  }
}

const sync = () => setGeometry((previous) => read(previous))

function attach() {
  const view = window.visualViewport ?? null
  window.addEventListener("resize", sync, { passive: true })
  window.addEventListener("orientationchange", sync, { passive: true })
  window.addEventListener("pageshow", sync, { passive: true })
  view?.addEventListener("resize", sync)
  view?.addEventListener("scroll", sync)
  attached = {
    view,
    detach() {
      window.removeEventListener("resize", sync)
      window.removeEventListener("orientationchange", sync)
      window.removeEventListener("pageshow", sync)
      view?.removeEventListener("resize", sync)
      view?.removeEventListener("scroll", sync)
    },
  }
}

function acquire() {
  subscribers += 1
  // Reattach if the VisualViewport object was replaced (test environments).
  if (attached && attached.view !== (window.visualViewport ?? null)) {
    attached.detach()
    attached = undefined
  }
  // Live listeners already hold the current geometry. Reading it again would
  // make the browser lay out the page while the new reader mounts.
  if (attached) return
  attach()
  sync()
}

function release() {
  subscribers -= 1
  if (subscribers > 0 || !attached) return
  attached.detach()
  attached = undefined
  // The next subscriber starts from the viewport as it is then.
  setGeometry(undefined)
}

/** Current viewport geometry while `active`; undefined otherwise. */
export function createVisualViewportGeometry(active: Accessor<boolean> = () => true): Accessor<VisualViewportGeometry | undefined> {
  const [subscribed, setSubscribed] = createSignal(false)
  createEffect(() => {
    if (!active() || typeof window === "undefined") {
      setSubscribed(false)
      return
    }
    acquire()
    setSubscribed(true)
    onCleanup(() => {
      setSubscribed(false)
      release()
    })
  })
  return () => (subscribed() ? geometry() : undefined)
}
